import { useEffect, useRef, useState } from "react";
import "../../../../../../styles/documents/modals/text-document-modal.css";
import { useIsMobile } from "../../../../../../hooks/useMediaQuery";
import { LinkPicker, LinkOption } from "../../../../../atoms";
import api from "../../../../../../utils/api";
import { API_ENDPOINTS } from "../../../../../../config";
import { DOCUMENT_CATEGORIES } from "../../../constants";
import { showToast } from "../../../../../../utils/toastManager";
import { onKanbansRefresh } from "../../../../../../utils/subscriptionEvents";
import html2pdf from "html2pdf.js";

export type TextDocData = {
  title: string;
  category: string;
  description: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  content: string;
  boardLinked?: string;
  jobLinked?: string;
};

interface Kanban {
  uid: string;
  name: string;
}

interface Job {
  uid: string;
  job_title: string;
  company_name?: string;
}

export interface TextDocumentModalProps {
  open: boolean;
  onClose: () => void;
  savedValue: TextDocData;
  onCommit: (v: TextDocData) => void;
}

const CATEGORY_OPTIONS = DOCUMENT_CATEGORIES;

export default function TextDocumentModal({
  open,
  onClose,
  savedValue,
  onCommit,
}: TextDocumentModalProps) {
  const isMobile = useIsMobile();
  const [title, setTitle] = useState('');
  const [docCategory, setDocCategory] = useState('Resume');
  const [description, setDescription] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [content, setContent] = useState('');
  const [catOpen, setCatOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const [boards, setBoards] = useState<LinkOption[]>([]);
  const [jobs, setJobs] = useState<LinkOption[]>([]);
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [boardLinked, setBoardLinked] = useState<string | undefined>();
  const [jobLinked, setJobLinked] = useState<string | undefined>();
  const boardLinkedRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!open) return;
    setTitle(savedValue.title || '');
    setDocCategory(savedValue.category || 'Resume');
    setDescription(savedValue.description || '');
    setFirstName(savedValue.firstName || '');
    setLastName(savedValue.lastName || '');
    setEmail(savedValue.email || '');
    setPhone(savedValue.phone || '');
    setContent(savedValue.content || '');
    setBoardLinked(savedValue.boardLinked);
    setJobLinked(savedValue.jobLinked);
    boardLinkedRef.current = savedValue.boardLinked;
  }, [open, savedValue]);

  useEffect(() => {
    if (open) {
      fetchBoards();
    }
  }, [open]);

  useEffect(() => {
    const unsubscribe = onKanbansRefresh(() => {
      if (open) {
        fetchBoards();
      }
    });
    return unsubscribe;
  }, [open]);

  useEffect(() => {
    if (boardLinked) {
      fetchJobs(boardLinked);
    } else {
      setJobs([]);
      setJobLinked(undefined);
    }
  }, [boardLinked]);

  const fetchBoards = async () => {
    try {
      setLoadingBoards(true);
      const data = await api.get<Kanban[]>(API_ENDPOINTS.kanbans.list);
      const boardOptions: LinkOption[] = data.map(kanban => ({
        id: kanban.uid,
        label: kanban.name
      }));
      setBoards(boardOptions);
      
      const currentBoardLinked = boardLinkedRef.current;
      if (currentBoardLinked) {
        const boardExists = boardOptions.some(b => b.id === currentBoardLinked);
        if (!boardExists) {
          setBoardLinked(undefined);
          boardLinkedRef.current = undefined;
          setJobLinked(undefined);
          setJobs([]);
        }
      }
    } catch (error) {

      setBoards([]);
    } finally {
      setLoadingBoards(false);
    }
  };

  const fetchJobs = async (kanbanUid: string) => {
    try {
      setLoadingJobs(true);
      const data = await api.get<Job[]>(API_ENDPOINTS.jobs.listByKanban(kanbanUid));
      const jobOptions: LinkOption[] = data.map(job => ({
        id: job.uid,
        label: job.company_name ? `${job.job_title} at ${job.company_name}` : job.job_title
      }));
      setJobs(jobOptions);
    } catch (error) {

      setJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleBoardChange = (boardId: string | null) => {
    const newBoardId = boardId || undefined;
    boardLinkedRef.current = newBoardId;
    setBoardLinked(newBoardId);
    setJobLinked(undefined);
  };

  const handleJobChange = (jobId: string | null) => {
    setJobLinked(jobId || undefined);
  };

  const canCreate = title.trim() !== '';

  const handleCreate = () => {
    if (!canCreate) {
      showToast('Enter a title for the document', 'error');
      return;
    }
    onCommit({
      title,
      category: docCategory,
      description,
      firstName,
      lastName,
      email,
      phone,
      content,
      boardLinked,
      jobLinked,
    });
  };

  const createButton = (
    <button
      className="tdm-btn tdm-btn--primary"
      onClick={handleCreate}
      disabled={!canCreate}
      title={canCreate ? undefined : 'Enter a title first'}
    >
      Create
    </button>
  );

  const copy = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(content);
        showToast('Text copied to clipboard', 'success');
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = content;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        textArea.setAttribute('readonly', '');
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          const successful = document.execCommand('copy');
          if (successful) {
            showToast('Text copied to clipboard', 'success');
          } else {
            showToast('Failed to copy text. Please try again.', 'error');
          }
        } catch (err) {
          showToast('Failed to copy text. Please try again.', 'error');
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      showToast('Failed to copy text. Please try again.', 'error');
    }
  };

  const exportToPDF = () => {
    try {
      const { jsPDF } = require('jspdf');
      const pdf = new jsPDF({
        unit: 'in',
        format: [8.5, 11],
        orientation: 'portrait'
      });

      const margin = 0.5;
      const pageWidth = 8.5;
      const pageHeight = 11;
      const contentWidth = pageWidth - (margin * 2);
      let yPosition = margin;

      const addText = (text: string, fontSize: number, isBold: boolean = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        
        const lines = pdf.splitTextToSize(text, contentWidth);
        const lineHeight = fontSize / 72;
        
        if (yPosition + (lines.length * lineHeight) > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }
        
        lines.forEach((line: string) => {
          pdf.text(line, margin, yPosition);
          yPosition += lineHeight;
        });
        
        return yPosition;
      };

      const fullName = [firstName, lastName].filter(Boolean).join(' ');
      if (fullName) {
        yPosition = addText(fullName, 18, true);
        yPosition += 0.15;
      }

      const contactInfo = [email, phone].filter(Boolean);
      if (contactInfo.length > 0) {
        yPosition = addText(contactInfo.join(' | '), 11, false);
        yPosition += 0.25;
      }

      if (content) {
        const contentLines = content.split('\n');
        contentLines.forEach(line => {
          if (line.trim()) {
            yPosition = addText(line, 12, false);
            yPosition += 0.1;
          } else {
            yPosition += 0.1;
          }
        });
      }

      const filename = title || 'document';
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
      
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
      
      showToast('PDF exported successfully', 'success');
    } catch (error) {
      console.error('PDF export error:', error);
      showToast('Failed to export PDF. Please try again.', 'error');
    }
  };

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (catRef.current && !catRef.current.contains(t)) setCatOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  if (!open) return null;

  return (
    <div className="tdm-overlay">
      <div className="tdm-overlay-bg" onClick={onClose} />
      <div className="tdm-modal">
        <div className="tdm-grid">
          <div className="tdm-left">
            <div className="tdm-header">
              <div className="tdm-header-title">Cover Letter</div>
              <div className="tdm-header-actions">
                <button 
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    copy();
                  }} 
                  className="tdm-btn"
                >
                  Copy to Clipboard
                </button>
                <button 
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    exportToPDF();
                  }} 
                  className="tdm-btn"
                >
                  Export to PDF
                </button>
                <button
                  type="button"
                  className="tdm-btn tdm-btn--close"
                  onClick={onClose}
                  aria-label="Close"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="tdm-divider" />
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="tdm-textarea"
            />
          </div>

          <div className="tdm-right">
            <div className="tdm-right-header">
              <div className="tdm-right-title">Create Document</div>
              {!isMobile && (
                <div className="tdm-header-actions">{createButton}</div>
              )}
            </div>
            <div className="tdm-divider" />

            <label className="tdm-label">
              Title <span className="tdm-label-required">Required</span>
            </label>
            <input
              className="tdm-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            <label className="tdm-label">
              Document Category <span className="tdm-label-required">Required</span>
            </label>
            <div style={{ position: 'relative', marginBottom: '12px' }} ref={catRef}>
              <button
                type="button"
                className="tdm-select-button"
                onClick={() => setCatOpen((v) => !v)}
              >
                <span className="tdm-select-text">{docCategory}</span>
                <span className="tdm-select-arrow">▾</span>
              </button>
              {catOpen && (
                <div className="tdm-dropdown">
                  {CATEGORY_OPTIONS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className="tdm-dropdown-item"
                      onClick={() => {
                        setDocCategory(c);
                        setCatOpen(false);
                      }}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <label className="tdm-label">
              Description
            </label>
            <textarea
              className="tdm-textarea-small"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ marginBottom: '12px' }}
            />

            <LinkPicker
              label="Boards"
              placeholder={loadingBoards ? "Loading boards..." : "Link board"}
              options={boards}
              value={boardLinked || null}
              onChange={handleBoardChange}
              disabled={loadingBoards}
            />

            <div style={{ marginTop: '12px', marginBottom: '12px' }}>
              <LinkPicker
                label="Jobs"
                placeholder={
                  !boardLinked 
                    ? "Select a board first" 
                    : loadingJobs 
                      ? "Loading jobs..." 
                      : "Link job"
                }
                options={jobs}
                value={jobLinked || null}
                onChange={handleJobChange}
                disabled={!boardLinked || loadingJobs}
              />
            </div>

            <div className="tdm-section-title">PDF Export Settings</div>
            <div className="tdm-section-text">
              Select what info you'd like in your PDF document header. Then
              click 'Export to PDF'.
            </div>

            <label className="tdm-label">
              First Name
            </label>
            <input
              className="tdm-input"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />

            <label className="tdm-label">
              Last Name
            </label>
            <input
              className="tdm-input"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />

            <label className="tdm-label">Email</label>
            <input
              className="tdm-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <label className="tdm-label">
              Phone Number
            </label>
            <input
              className="tdm-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />

            {isMobile && (
              <div className="tdm-sheet-actions">{createButton}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}